import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import {
  BarChart, Card, LineChart, SectionHead, Steps,
} from '../../PipelineComponents';
import {
  formattedCount, formattedDays, funnelStage, percentOf,
} from '../../../pipelineHelpers';

const transferOpportunitySteps = [
  <>
    <b>Transfer lifecycle status on every script</b>
    {' '}
    - requested, received, processed, filled, failed. Without it, fall-off cannot be counted at all.
  </>,
  <>
    <b>Required close reason.</b>
    {' '}
    No transfer closes as
    {' '}
    <code>other</code>
    {' '}
    - unexplained closures become measurable.
  </>,
  <>
    <b>Day-3 SLA clock.</b>
    {' '}
    Unanswered requests surface automatically instead of waiting on memory - normally the largest
    fall-off bucket.
  </>,
  <>
    <b>Verify pharmacy and contact detail at intake.</b>
    {' '}
    Removes the accuracy failures found only after sending.
  </>,
  <>
    <b>Auto-route failures into the pipeline</b>
    {' '}
    - a failed transfer becomes an open member record with a reason, not a dead end.
  </>,
];

const FUNNEL_TIPS = {
  requested: {
    text: 'Transfers asked for during the period.',
    steps: ['Counted from the transfer date recorded on the prescription.'],
    note: 'Section 03 shows members waiting on a warm transfer. If nothing appears here, the transfers are'
      + ' happening - they are just not being recorded as transfers on the prescription.',
    warn: true,
  },
  received: {
    text: 'Transfers where the prescription arrived from the other pharmacy.',
    steps: ['Counted once the status moves to received.'],
    note: 'Nothing can appear here until the step above is being captured.',
  },
  processed: {
    text: 'Transfers the pharmacy has worked on.',
    steps: ['Counted once the status moves to processing.'],
    note: 'Same again - every step in this funnel depends on the first one existing.',
  },
  filled: {
    text: 'Transfers that reached the member.',
    steps: ['Counted once the prescription is filled.'],
    note: 'This is the number that shows how many members actually got their medication through a transfer.'
      + ' It is the whole point of the section.',
  },
};

const FALL_OFF_TIP = {
  text: 'Transfers that never reached the member, grouped by the reason recorded.',
  steps: ['Counted once per transfer, from the reason kept on the record.'],
  note: 'A reason only appears here when it is recorded on the transfer - anything closed without one is'
    + ' missing from this chart entirely.',
};

const TransferGaps = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [summary, setSummary] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getTransferGaps(params);

        setSummary(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const { stages, timings, fallOffReasons } = useMemo(() => {
    const funnel = summary?.funnel;

    const requested = Number(funnel?.requested) || 0;

    return {
      stages: funnel
        ? [
          funnelStage('Requested', requested, percentOf(requested, requested), FUNNEL_TIPS.requested),
          funnelStage('Received', funnel.received, percentOf(funnel.received, requested), FUNNEL_TIPS.received),
          funnelStage('Processed', funnel.processed, percentOf(funnel.processed, requested), FUNNEL_TIPS.processed),
          funnelStage('Filled', funnel.filled, percentOf(funnel.filled, requested), FUNNEL_TIPS.filled),
        ]
        : [],

      timings: [
        { label: 'Avg request → received', value: formattedDays(funnel?.avgDaysRequestToReceive) },
        { label: 'Avg received → filled', value: formattedDays(funnel?.avgDaysReceiveToFill) },
        // { label: 'Fell off', value: formattedCount(funnel?.fallOff) },
        // { label: 'Fall-off rate', value: formattedPercent(funnel?.fallOffPct) },
      ],

      fallOffReasons: (summary?.fallOffReasons || []).map((d) => ({
        label: d.reason,
        value: +(d.count || 0),
        caption: formattedCount(d.count),
        tooltip: FALL_OFF_TIP,
      })),
    };
  }, [summary]);

  return (
    <>
      <SectionHead section={section} />

      <div className={`${classes.row} ${classes.row_3}`}>
        <Card
          title="Transfer funnel"
          caption="Transfers requested in the period"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!stages.length}
        >
          <BarChart
            data={stages}
            className={classes.bordered}
          />

          <div className={classes.kv_list}>
            {timings.map((item) => (
              <div className={classes.kv} key={item.label}>
                {item.label}

                <span className={classes.kv_value}>{item.value}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card
          title="Transfer turnaround time"
          caption="How long each transfer took"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!fallOffReasons.length}
        >
          <LineChart data={fallOffReasons} />
        </Card>

        <Card
          title="Capturing the opportunity"
          caption="What each gap needs in the system"
        >
          <Steps
            items={transferOpportunitySteps}
            className={classes.bordered}
          />

          <p className={classes.note}>
            Recovering half of the fall-offs puts members back on therapy every quarter, with the
            gap days in section 01 as the measure of the impact.
          </p>
        </Card>
      </div>

      {/* <div className={`${classes.row} ${classes.row_2u}`}> */}
      {/*  <TransfersStatsCard params={params} /> */}

      {/*  <TransfersListCard /> */}
      {/* </div> */}
    </>
  );
};

TransferGaps.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default TransferGaps;
